import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
} from "firebase/auth";
import { useFirebase } from "../FirebaseContext";
import { getFirebaseInstance } from "../../utils/firebase/config";
import { getFirestore } from "firebase/firestore";
import {
  getUserByEmail,
  createOrUpdateUser,
  USER_ROLES,
} from "../../utils/roles/roleUtils";
import LoadingSpinner from "../../components/ui/LoadingSpinner";

export const RoleContext = createContext();

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error("useRole must be used within a RoleProvider");
  }
  return context;
};

export const RoleProvider = ({ children }) => {
  const { auth, db, isReady, tenant } = useFirebase();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [defaultDb, setDefaultDb] = useState(null);

  useEffect(() => {
    if (isReady) {
      const defaultFirebaseAppInstance = getFirebaseInstance("default");
      setDefaultDb(getFirestore(defaultFirebaseAppInstance.app));
    }
  }, [isReady]);

  useEffect(() => {
    if (!isReady || !auth || !db || !defaultDb) {
      // Add defaultDb to condition
      setLoading(true);
      return;
    }

    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      setLoading(true);

      try {
        if (firebaseUser) {
          // User is signed in, check if they're authorized using the default tenant's DB
          const userData = await getUserByEmail(defaultDb, firebaseUser.email);

          if (userData && userData.isActive) {
            // User exists and is active
            setUser(firebaseUser);
            setUserRole(userData);
            setAuthorized(true);

            // Update last login using the default tenant's DB (reusing already fetched userData)
            await createOrUpdateUser(
              defaultDb,
              {
                email: firebaseUser.email,
                displayName: firebaseUser.displayName,
                photoURL: firebaseUser.photoURL,
                role: userData.role,
              },
              userData,
            );
          } else {
            // User not authorized or doesn't exist
            setUser(firebaseUser);
            setUserRole(null);
            setAuthorized(false);
          }
        } else {
          // User is signed out
          setUser(null);
          setUserRole(null);
          setAuthorized(false);
        }
      } catch (error) {
        console.error("Error in auth state change:", error);
        setUser(null);
        setUserRole(null);
        setAuthorized(false);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [auth, db, isReady, defaultDb]); // Add defaultDb to dependencies

  const signIn = useCallback(async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("email");
      provider.addScope("profile");

      const result = await signInWithPopup(auth, provider);
      return result.user;
    } catch (error) {
      console.error("Error signing in:", error);
      throw error;
    }
  }, [auth]);

  const signOut = useCallback(async () => {
    try {
      await auth.signOut();
      setUser(null);
      setUserRole(null);
      setAuthorized(false);
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  }, [auth]);

  const refreshUserRole = useCallback(async () => {
    if (user && defaultDb) {
      // Conditionally use defaultDb
      try {
        const userData = await getUserByEmail(defaultDb, user.email);
        setUserRole(userData);
        setAuthorized(userData && userData.isActive);
      } catch (error) {
        console.error("Error refreshing user role:", error);
      }
    }
  }, [user, defaultDb]);

  const isAdmin = useCallback(() => {
    return userRole && userRole.role === USER_ROLES.ADMIN;
  }, [userRole]);

  const isSharedUser = useCallback(() => {
    return userRole && userRole.role === USER_ROLES.SHARED;
  }, [userRole]);

  const isPublicAccess = useCallback(() => {
    return !user || (userRole && userRole.role === USER_ROLES.PUBLIC);
  }, [user, userRole]);

  const hasRole = useCallback((role) => {
    return userRole && userRole.role === role;
  }, [userRole]);

  // e2e purpose: expose signInWithCustomTokenForTest in non-production environments
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && auth) {
      window.signInWithCustomTokenForTest = async (token) => {
        try {
          await signInWithCustomToken(auth, token);
          // onAuthStateChanged will handle the rest.
        } catch (error) {
          console.error("Error signing in with custom token for test:", error);
        }
      };

      // Cleanup
      return () => {
        delete window.signInWithCustomTokenForTest;
      };
    }
  }, [auth]);

  const value = useMemo(
    () => ({
      user,
      userRole,
      loading,
      authorized,
      tenant,
      signIn,
      signOut,
      refreshUserRole,
      isAdmin,
      isSharedUser,
      isPublicAccess,
      hasRole,
    }),
    [
      user,
      userRole,
      loading,
      authorized,
      tenant,
      signIn,
      signOut,
      refreshUserRole,
      isAdmin,
      isSharedUser,
      isPublicAccess,
      hasRole,
    ],
  );

  if (loading) {
    return (
      <LoadingSpinner
        fullScreen
        size={60}
        message="Verificando autorização..."
      />
    );
  }

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export default RoleContext;
